/**
 * DriveDesk Hub Presentation Mode — Guided Tour step definitions.
 *
 * Philosophy (per design_guidelines.md `guided_tour_system`): every stop
 * answers "why does this matter?" instead of "what button is this?". A
 * step with `targetTestId: null` is a "concept" stop — it's shown as a
 * centered card with a dimmed backdrop but no spotlight cutout, used
 * for differentiators that exist on a role the current viewer isn't
 * logged in as (e.g. describing the Parent Portal to an Admin) rather
 * than skipping the idea entirely.
 *
 * `getTourSteps(role)` filters + flattens this list into the exact,
 * ordered sequence for one role — keeping the whole tour role-aware
 * without duplicating a separate config per role.
 */
export const TOUR_STEPS = [
  {
    id: "daily_brief",
    roles: ["admin", "office_staff", "instructor", "parent"],
    route: "/",
    targetTestId: "daily-brief-card",
    title: "Daily Brief",
    body: "Instead of opening six different screens every morning, DriveDesk Hub summarizes what needs your attention first — in one sentence.",
  },
  {
    id: "business_pulse",
    roles: ["admin", "office_staff"],
    route: "/",
    targetTestId: "business-pulse-card",
    title: "Business Pulse",
    body: "Gives owners an instant health check of the business, highlighting trends before they become problems — not just a report you have to interpret.",
  },
  {
    id: "todays_priorities",
    roles: ["admin", "office_staff", "instructor", "parent"],
    route: "/",
    targetTestId: "todays-priorities-card",
    title: "Today's Priorities",
    body: "A short, always-current list of what actually needs a decision today — never a wall of tasks you have to sort through yourself.",
  },
  {
    id: "insights_recommendations",
    roles: ["admin", "office_staff", "instructor", "parent"],
    route: "/",
    targetTestId: "insights-recommendations-module",
    title: "Insights & Recommendations",
    body: "DriveDesk Hub doesn't just show data — it tells you what it means and what to do next, so nobody has to interpret a spreadsheet to find the next best action.",
  },
  {
    id: "drivedesk_ai",
    roles: ["admin", "office_staff", "instructor", "parent"],
    route: "/",
    targetTestId: "ai-assistant-launcher",
    title: "🤖 Your AI Office Manager",
    body: "Unlike a traditional chatbot, Your AI Office Manager understands your school's real data — it answers questions, drafts communications, and prepares business actions for your approval.",
  },
  {
    id: "ai_action_plans",
    roles: ["admin", "office_staff", "instructor"],
    route: "/",
    targetTestId: "ai-assistant-launcher",
    title: "AI Action Plans",
    body: "Tell it something like \u201cI'm sick tomorrow\u201d and Your AI Office Manager prepares a full coverage plan \u2014 affected lessons, replacement instructors, drafted parent messages \u2014 and waits for your approval. Nothing changes until you click Approve.",
  },
  {
    id: "role_dashboard_admin",
    roles: ["admin", "office_staff"],
    route: "/",
    targetTestId: "admin-dashboard-page",
    title: "Your Command Center",
    body: "One dashboard gives owners and office staff a complete view of the business \u2014 schedules, payments, students, and staff \u2014 without switching between five different tools.",
  },
  {
    id: "role_dashboard_instructor",
    roles: ["instructor"],
    route: "/",
    targetTestId: "instructor-dashboard-page",
    title: "Built Around Your Day",
    body: "Instructors see exactly what matters \u2014 today's lessons, vehicle status, and student notes \u2014 with nothing extra to dig through before a lesson.",
  },
  {
    id: "role_dashboard_parent",
    roles: ["parent"],
    route: "/",
    targetTestId: "parent-dashboard-page",
    title: "A Portal Built for Parents",
    body: "Parents get a clear, always-current view of their child's progress, balance, and upcoming lessons \u2014 no phone calls to the office needed.",
  },
  {
    id: "scheduling",
    roles: ["admin", "office_staff"],
    route: "/schedule",
    targetTestId: "schedule-page",
    title: "Scheduling",
    body: "Lessons, classroom sessions, and instructor availability all live on one calendar \u2014 conflicts get caught automatically instead of discovered later.",
  },
  {
    id: "parent_portal_concept",
    roles: ["admin", "office_staff", "instructor"],
    route: null,
    targetTestId: null,
    title: "Parent Portal",
    body: "Parents get their own portal \u2014 progress, balances, forms, and messages \u2014 so your office spends less time on \u201cwhere are we?\u201d phone calls.",
  },
  {
    id: "student_progress_parent",
    roles: ["parent"],
    route: "/",
    targetTestId: "student-progress-cards-grid",
    title: "Student Progress",
    body: "Progress isn't just tracked \u2014 it's interpreted, so it's easy to see who's nearly road-test ready and who needs a bit more instruction.",
  },
  {
    id: "student_progress_concept",
    roles: ["admin", "office_staff", "instructor"],
    route: null,
    targetTestId: null,
    title: "Student Progress",
    body: "Progress isn't just tracked \u2014 it's interpreted. Every student gets a clear, plain-English readout of how close they are to road-test ready.",
  },
  {
    id: "parent_quick_actions",
    roles: ["parent"],
    route: "/",
    targetTestId: "parent-quick-actions",
    title: "Everything at a Glance",
    body: "Balance, forms, and next steps are always visible \u2014 even when everything is already taken care of \u2014 so there's never a guessing game.",
  },
  {
    id: "reports_insights",
    roles: ["admin", "office_staff"],
    route: "/reports",
    targetTestId: "reports-page",
    title: "Reports & Insights",
    body: "Instructor hours, attendance, no-shows, payments, and fleet maintenance \u2014 all in one place, so you can spot patterns instead of hunting for them.",
  },
];

export function getTourSteps(role) {
  return TOUR_STEPS.filter((step) => step.roles.includes(role));
}
